import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Tooltip,
} from "@mui/material";

import { Editor } from "@tinymce/tinymce-react";

import axios from "axios";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

const API = process.env.REACT_APP_API_URL;

export default function AccountManager() {
  const [accounts, setAccounts] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [replySignatureHtml, setReplySignatureHtml] = useState("");

  // Mostrar las firma logo
  const [logoPath, setLogoPath] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    const res = await axios.get(`${API}/auth/accounts`);
    setAccounts(res.data);
  };

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return "#" + "000000".substring(0, 6 - c.length) + c;
  }

  const openGoogleOAuth = () => {
    window.location.href = `${API}/auth/google`;
  };

  const openEditDialog = async (acc) => {
    setEditingAccount(acc);

    try {
      const res = await axios.get(`${API}/signatures/${acc.email}`);
      setSignatureHtml(res.data.signature_html || "");
      setReplySignatureHtml(res.data.reply_signature_html || "");
    } catch (err) {
      if (err.response?.status === 404) {
        // crear fila vacía automáticamente
        await axios.put(`${API}/signatures/${acc.email}`, {
          signature_html: "",
          reply_signature_html: "",
        });

        setSignatureHtml("");
        setReplySignatureHtml("");
        setLogoPath(null); // <-- mejor poner null
      }
    }
    setOpenEdit(true);
  };

  const saveSignatures = async () => {
    await axios.put(`${API}/signatures/${editingAccount.email}`, {
      signature_html: signatureHtml,
      reply_signature_html: replySignatureHtml,
    });

    setOpenEdit(false);
    loadAccounts();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await axios.post(
        `${API}/signatures/upload-logo/${editingAccount.email}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert("Logo subido correctamente ✔️");

      if (res.data?.logo_path) {
        const fullUrl = `${API}${res.data.logo_path}`;
        setLogoPath(fullUrl);

        // Insertar la imagen real en TinyMCE
        if (!signatureHtml.includes(fullUrl)) {
          setSignatureHtml(
            signatureHtml +
              `<br/><img src="${fullUrl}" style="max-width:180px;" />`
          );
        }
      }
    } catch (err) {
      console.error(err);
      alert("Error al subir logo");
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm("¿Eliminar esta cuenta?")) return;
    await axios.delete(`${API}/auth/account/${id}`);
    loadAccounts();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, mb: 2 }}>
        Gestión de cuentas de correo
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        sx={{ backgroundColor: "#69cbf9", color: "#000", mb: 3 }}
        onClick={openGoogleOAuth}
      >
        Añadir cuenta
      </Button>

      <Divider sx={{ mb: 2 }} />

      <List sx={{ mt: 1 }}>
        {accounts.map((acc) => (
          <ListItem
            key={acc.id}
            sx={{
              mb: 1,
              borderRadius: "10px",
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
            }}
            secondaryAction={
              <>
                <Tooltip title="Editar firmas">
                  <IconButton onClick={() => openEditDialog(acc)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Eliminar cuenta">
                  <IconButton onClick={() => deleteAccount(acc.id)}>
                    <DeleteIcon sx={{ color: "#d9534f" }} />
                  </IconButton>
                </Tooltip>
              </>
            }
          >
            <ListItemAvatar>
              <Avatar
                sx={{
                  bgcolor: stringToColor(acc.email),
                  width: 40,
                  height: 40,
                  fontSize: 14,
                }}
              >
                {acc.email[0].toUpperCase()}
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={acc.email}
              secondary={`ID: ${acc.id} • Historial: ${
                acc.last_history_id ? acc.last_history_id : "—"
              }`}
            />
          </ListItem>
        ))}
      </List>

      {/* DIALOGO DE EDICIÓN */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar firmas — {editingAccount?.email}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Firma estándar
          </Typography>

          <Editor
            apiKey="3wlnbj1d6oyd7czfh9l7yh3yh9dnhvdh2t5ovk1m4dgdtaxl"
            value={signatureHtml}
            init={{
              height: 300,
              menubar: true,
              plugins:
                "advlist autolink lists link image charmap preview anchor " +
                "searchreplace visualblocks code fullscreen " +
                "insertdatetime media table paste help wordcount",
              toolbar:
                "undo redo | formatselect | bold italic underline | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | link image | removeformat",
              branding: false,
            }}
            onEditorChange={(content) => setSignatureHtml(content)}
          />

          <Button variant="outlined" component="label" sx={{ mb: 3 }}>
            Subir Logo
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleLogoUpload(e)}
            />
          </Button>
          {logoPath && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Vista previa del logo
              </Typography>

              <img
                /* src={`http://localhost:4000${logoPath}`} */
                src={logoPath}
                alt="Firma Logo"
                style={{
                  maxWidth: "180px",
                  border: "1px solid #ddd",
                  padding: 4,
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
            </Box>
          )}

          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Firma para respuestas
          </Typography>

          <Editor
            apiKey="3wlnbj1d6oyd7czfh9l7yh3yh9dnhvdh2t5ovk1m4dgdtaxl"
            value={replySignatureHtml}
            init={{
              height: 200,
              menubar: false,
              plugins: "link lists",
              toolbar: "bold italic underline | bullist numlist | link",
              branding: false,
            }}
            onEditorChange={(content) => setReplySignatureHtml(content)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#69cbf9", color: "#000" }}
            onClick={saveSignatures}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
